/**
 * Date utility functions for consistent date handling across the application
 * Handles multiple date formats: ISO (YYYY-MM-DD), UK (DD/MM/YYYY), US (MM/DD/YYYY)
 */

/**
 * Parse a date string in various formats to ISO format (YYYY-MM-DD)
 * @param {string} dateStr - Date string in various formats
 * @returns {string|null} - ISO date string or null if invalid
 */
export const parseToISO = (dateStr) => {
  if (!dateStr) return null;
  
  // Already ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.substring(0, 10);
  }
  
  // UK format DD/MM/YYYY (day first)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    // Validate: day should be 1-31, month should be 1-12
    if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12) {
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }
  
  // Flexible format with single digits (D/M/YYYY or DD/M/YYYY etc)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const parts = dateStr.split('/');
    const first = parseInt(parts[0], 10);
    const second = parseInt(parts[1], 10);
    const year = parts[2];
    
    // Heuristic: if first number > 12, it must be a day (UK format)
    // if second number > 12, it must be a day (US format)
    // otherwise, assume UK format (DD/MM/YYYY) as default for this UK-based app
    let day, month;
    if (first > 12) {
      // Must be UK format: DD/MM/YYYY
      day = first;
      month = second;
    } else if (second > 12) {
      // Must be US format: MM/DD/YYYY
      month = first;
      day = second;
    } else {
      // Ambiguous - default to UK format for this app
      day = first;
      month = second;
    }
    
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  
  // Try to parse as Date object (handles ISO strings, etc.)
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }
  
  return null;
};

/**
 * Check if a date is overdue (before today)
 * @param {string} dateStr - Date string in various formats
 * @returns {boolean} - True if the date is before today
 */
export const isDateOverdue = (dateStr) => {
  const isoDate = parseToISO(dateStr);
  if (!isoDate) return false;
  
  const today = new Date().toISOString().split('T')[0];
  return isoDate < today;
};

/**
 * Check if a date is today
 * @param {string} dateStr - Date string in various formats
 * @returns {boolean} - True if the date is today
 */
export const isDateToday = (dateStr) => {
  const isoDate = parseToISO(dateStr);
  if (!isoDate) return false;
  
  const today = new Date().toISOString().split('T')[0];
  return isoDate === today;
};

/**
 * Calculate days overdue (positive) or days until due (negative)
 * @param {string} dateStr - Date string in various formats
 * @returns {number} - Number of days (positive = overdue, negative = future)
 */
export const getDaysFromDue = (dateStr) => {
  const isoDate = parseToISO(dateStr);
  if (!isoDate) return 0;
  
  const dueDate = new Date(isoDate + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = today - dueDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Format a date for display in UK format (DD/MM/YYYY)
 * @param {string} dateStr - Date string in various formats
 * @returns {string} - Formatted date string or empty string if invalid
 */
export const formatDateUK = (dateStr) => {
  const isoDate = parseToISO(dateStr);
  if (!isoDate) return '';
  
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Get today's date in ISO format
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const getTodayISO = () => {
  return new Date().toISOString().split('T')[0];
};
